import { useCallback, useEffect, useRef, useState } from "react";
import { listJobs } from "../../../api/migration";
import { extractApiError } from "../helpers";
import type { MigrationJob } from "../../../type";

interface UseMigrationJobsResult {
  jobs: MigrationJob[];
  count: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  hasActiveJobs: boolean;
}

/**
 * Mirrors useAdminUsers's stale-response guard (monotonic seq ref). The tab
 * that owns this hook decides when to activate polling — `hasActiveJobs`
 * tells it whether any job is still in flight (pending/scheduled/running).
 */
export default function useMigrationJobs(active: boolean): UseMigrationJobsResult {
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reloadSeqRef = useRef(0);

  const reload = useCallback(async () => {
    if (!active) return;
    const mySeq = ++reloadSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await listJobs({ page });
      if (mySeq !== reloadSeqRef.current) return;
      setJobs(data.results);
      setCount(data.count);
    } catch (err) {
      if (mySeq !== reloadSeqRef.current) return;
      setError(extractApiError(err, "Failed to load migration jobs."));
    } finally {
      if (mySeq === reloadSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [page, active]);

  useEffect(() => {
    reload();
  }, [reload]);

  const hasActiveJobs = jobs.some((j) =>
    j.status === "pending" || j.status === "scheduled" || j.status === "running"
  );

  // Poll while any job is live. 3s matches the CLAUDE.md guideline for
  // UI-facing status polls: fast enough to feel live, slow enough that the
  // worker's short copy jobs (~200ms) don't spam the API.
  useEffect(() => {
    if (!active || !hasActiveJobs) return;
    const id = window.setInterval(() => {
      reload();
    }, 3000);
    return () => window.clearInterval(id);
  }, [active, hasActiveJobs, reload]);

  return {
    jobs,
    count,
    page,
    setPage,
    isLoading,
    error,
    reload,
    hasActiveJobs,
  };
}
